import React, { useMemo } from 'react';
import type { Track } from '../services/trackTypes';
import { TRACK_STATUS_VALUES, groupTracksByStatus, getStatusColor, type KanbanSort } from '../services/kanbanService';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
//
import { useTranslation } from 'react-i18next';
import TrackTileKanban from './TrackTileKanban';
import type { AudioFile } from '../services/trackTypes';
import { useDesignMode } from '../contexts/DesignContext';
import { classNames } from '../utils/classNames';

export interface KanbanBoardProps {
  tracks: Track[];
  sortBy: KanbanSort;
  isEditable: (track: Track) => boolean;
  onMove: (trackId: string, toStatus: string) => Promise<void>;
  movingIds?: Set<string>;
  // Artwork props
  artworkBlobs: { [trackId: string]: string | null };
  artworkLoading: { [trackId: string]: boolean };
  onArtworkClick: (trackId: string, url: string) => void;
  // Audio player props
  playingTrackId: string | null;
  isPaused?: boolean;
  onTogglePlay: (trackId: string) => void;
  // Delete props
  onDelete: (trackId: string) => Promise<void>;
  deletingTracks: Set<string>;
  // Sharing props
  sharedRole?: (track: Track) => 'viewer' | 'editor' | null;
  sharedCount?: (track: Track) => number | undefined;
  isOwner?: (track: Track) => boolean;
  ownerUserId?: (track: Track) => string | undefined;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ tracks, sortBy, isEditable, onMove, artworkBlobs, artworkLoading, onArtworkClick, playingTrackId, isPaused = false, onTogglePlay, onDelete, deletingTracks, sharedRole, isOwner }) => {
  const { t } = useTranslation();
  const { isSketchy } = useDesignMode();
  const grouped = useMemo(() => groupTracksByStatus(tracks, sortBy), [tracks, sortBy]);

  const onDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    const fromStatus = source.droppableId;
    const toStatus = destination.droppableId;
    if (fromStatus === toStatus) return;
    await onMove(draggableId, toStatus);
  };

  return (
    <div className="w-full flex flex-col gap-4 kanban-board">
      <DragDropContext onDragEnd={onDragEnd}>
        {TRACK_STATUS_VALUES.map((status) => {
          const { bg, text, border } = getStatusColor(status);
          const items = grouped[status] || [];
          const hasItems = items.length > 0;
          return (
            <div
              key={status}
              className={classNames(
                isSketchy ? 'sketch-box kanban-lane rounded-sm' : 'rounded-lg border'
              )}
              style={isSketchy ? undefined : { borderColor: border, backgroundColor: 'transparent' }}
              data-testid={`kanban-column-${status.toLowerCase()}`}
            >
              <div
                className={classNames('px-4 py-2 flex items-center justify-between', isSketchy ? 'label-typewriter border-b border-dashed border-[var(--color-border)]' : 'rounded-t-lg')}
                style={isSketchy ? { color: text } : { backgroundColor: bg, color: text, borderBottom: `1px solid ${border}` }}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold">{t(`track.statusLabels.${status}`, status)}</span>
                  <span className="text-xs opacity-75">{items.length}</span>
                </div>
              </div>
              <Droppable droppableId={status} direction="horizontal">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps} className={`px-3 pb-3 pt-1 ${hasItems ? 'min-h-[120px]' : 'min-h-[48px]'} transition-all`}>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {items.map((track, index) => {
                      const disabled = !isEditable(track);
                      const previewAudio = (track.audioFiles || []).find((f: AudioFile) => f.isPreview) || (track.audioFiles && track.audioFiles.length > 0 ? track.audioFiles[track.audioFiles.length - 1] : undefined);
                        const artworkUrl = artworkBlobs[track.id] && artworkBlobs[track.id] !== 'placeholder' ? artworkBlobs[track.id] : null;
                        return (
                          <Draggable key={track.id} draggableId={track.id} index={index} isDragDisabled={disabled}>
                            {(dragProvided, snapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                className={classNames(
                                  'w-full my-2',
                                  snapshot.isDragging && 'z-[9999]'
                                )}
                                style={{
                                  ...dragProvided.draggableProps.style,
                                  opacity: snapshot.isDragging ? 0.95 : 1,
                                }}
                              >
                                <TrackTileKanban
                                  track={track as any}
                                  isAccessible={true}
                                  isEditable={!disabled}
                                  t={t as any}
                                  artworkUrl={artworkUrl}
                                  artworkLoading={!!artworkLoading[track.id]}
                                  onArtworkClick={() => onArtworkClick(track.id, artworkBlobs[track.id]!)}
                                  deleting={deletingTracks.has(track.id)}
                                  previewAudio={previewAudio as any}
                                  isPlaying={playingTrackId === track.id && !isPaused}
                                  onTogglePlay={() => onTogglePlay(track.id)}
                                  onDelete={() => onDelete(track.id)}
                                  sharedRole={sharedRole?.(track)}
                                  isOwner={isOwner?.(track)}
                                  dragHandleProps={dragProvided.dragHandleProps}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                    {!hasItems && (
                      <div className="flex items-center gap-2 text-xs px-2 py-1" style={{ color: 'var(--color-muted)' }}>
                        {t('dashboard.dropHere') || 'Drop here'}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </DragDropContext>
    </div>
  );
};

export default KanbanBoard;


